import React, { useCallback, useMemo, useRef, useState } from 'react';

import { DOCUMENT_TYPES, PAGE_SIZE_SM } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ContainerBanner, ErrorResponse, Spinner, Toolbar } from '~components';
import { DateInput } from '~components/form-fields/inputs';
import Icon, { faFileCsv, faInfoCircle } from '~components/icon';
import { ColumnPickerModal, TableView, useColumnVisibility } from '~components/table-view';

import { CreateDocumentBatchJob, useCreateDocumentBatchMutation } from '~main/actions/documentBatches';
import { useGetAcfrItemsSpecsQuery, useLazyGetAcfrItemsQuery } from '~support-at-home/actions/sahAcfrItems';
import { AcfrItemRow } from '~support-at-home/models/acfrItems';
import { COLUMN_GROUPS, columnsFactory, exportColumnsFor } from '~support-at-home/forms/reports/acfrItemsList';

// Identifies this table's saved column preference row — must match tp-assist-api's table_key.
const TABLE_KEY = 'sah-acfr-items';

const DEFAULT_DATE_FROM = '2025-11-01';
const DEFAULT_DATE_TO = '2026-06-30';

/**
 * TAV-3318: the rows behind every ACFR report figure, one per budget entry.
 *
 * Run-report rather than a live grid, matching the ACFR report tab it explains: the query spans
 * a financial year and there is no useful default view of it.
 */
const ACFRItemsTab: React.FC<{ isActive?: boolean }> = ({ isActive = true }) => {
  const { showToast } = useToast();
  const [createDocumentBatch, { isLoading: isGenerating }] = useCreateDocumentBatchMutation();

  const [dateFrom, setDateFrom] = useState(DEFAULT_DATE_FROM);
  const [dateTo, setDateTo] = useState(DEFAULT_DATE_TO);
  // The range the visible results were actually run for. The date inputs change the moment a
  // user edits them, but the table still shows the previous range until Run Report is pressed,
  // so paging has to follow what was run rather than what is currently typed in.
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);
  // Every page loaded so far, and the cursor for the next one. Pages are appended rather than
  // replaced: the walk is cursor-only, so there is no jumping to page N, and a page can only be
  // reached from the one before it.
  const [rows, setRows] = useState<AcfrItemRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Bumped on every Run Report, so a page still in flight from the previous run cannot be
  // appended to the new one's results.
  const runIdRef = useRef(0);
  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);

  const {
    data: specsData,
    isLoading: isLoadingSpecs,
    isError: isErrorSpecs,
    error: errorSpecs,
    refetch: refetchSpecs,
  } = useGetAcfrItemsSpecsQuery(undefined, { skip: !isActive });

  const [fetchItems, { isFetching, isError, error, isUninitialized }] = useLazyGetAcfrItemsQuery();

  const {
    isEnabled: isColumnPickerEnabled,
    visibleColumnIds,
    setVisibleColumnIds,
  } = useColumnVisibility({
    tableKey: TABLE_KEY,
    columnSpecs: specsData?.columns,
    onPreferencesChanged: refetchSpecs,
    onError: (columnPreferenceError) => {
      const message =
        columnPreferenceError instanceof Error ? columnPreferenceError.message : 'Failed to save column preferences';
      showToast('danger', message);
    },
  });

  const columns = useMemo(() => columnsFactory(specsData?.columns ?? []), [specsData]);
  const exportColumns = useMemo(() => exportColumnsFor(columns), [columns]);

  // Cursor paging, the same walk the CSV export makes. Offset paging dropped rows: the API holds
  // back a group that might continue past the end of a page and resumes it from the cursor, and
  // an offset page starts at a fixed position regardless, so every held-back group fell on no page.
  const loadPage = useCallback(
    async (range: { from: string; to: string }, cursor: string | null) => {
      const runId = runIdRef.current;
      try {
        const page = await fetchItems({
          date_from: range.from,
          date_to: range.to,
          limit: PAGE_SIZE_SM,
          ...(cursor ? { cursor } : {}),
        }).unwrap();
        if (runId !== runIdRef.current) return;
        setRows((previous) => (cursor ? [...previous, ...page.rows] : page.rows));
        setNextCursor(page.next_cursor);
      } catch {
        // The query hook's error state renders the failure; nothing to add here.
      }
    },
    [fetchItems],
  );

  const handleRunReport = useCallback(() => {
    const range = { from: dateFrom, to: dateTo };
    runIdRef.current += 1;
    setAppliedRange(range);
    setRows([]);
    setNextCursor(null);
    loadPage(range, null);
  }, [loadPage, dateFrom, dateTo]);

  const handleLoadMore = useCallback(() => {
    if (!appliedRange || !nextCursor || isFetching) return;
    loadPage(appliedRange, nextCursor);
  }, [loadPage, appliedRange, nextCursor, isFetching]);

  const onGenerateCsv = async () => {
    try {
      // Export what the table is showing, not what is currently typed in: editing the dates
      // without pressing Run Report would otherwise hand back a file for a period the user never
      // saw. The live inputs are the fallback so an export still works before a report is run.
      const range = appliedRange ?? { from: dateFrom, to: dateTo };
      const job = {
        document_type: DOCUMENT_TYPES.SAH_ACFR_ITEMS.CSV,
        date_from: range.from,
        date_to: range.to,
        columns: exportColumns,
      } as CreateDocumentBatchJob & Record<string, unknown>;
      await createDocumentBatch([job]).unwrap();
      showToast('success', 'CSV generation started');
    } catch (csvError) {
      const message = csvError instanceof Error ? csvError.message : 'Failed to start CSV generation';
      showToast('danger', message);
    }
  };

  // A specs response that resolved but carries no columns (an old backend pod mid-rollout) is
  // indistinguishable from an error to the caller, so treat it as one rather than rendering a
  // table with no columns at all.
  const hasIncompleteSpecs = isActive && !isLoadingSpecs && !isErrorSpecs && !specsData?.columns;
  // Every column deselected would queue an export with nothing to put in it. The document
  // engine rejects that too, but stopping it here saves the user a failed job.
  const hasNoExportColumns = Boolean(specsData?.columns) && exportColumns.length === 0;

  const renderContent = () => {
    if (isLoadingSpecs) {
      return (
        <div className="loading" role="status" aria-live="polite">
          <Spinner loading={true} type="beat" size={10} />
          Loading columns...
        </div>
      );
    }

    if (isErrorSpecs || hasIncompleteSpecs) {
      const message = hasIncompleteSpecs
        ? 'Failed to load ACFR items - column data was unavailable'
        : `Failed to load ACFR items - ${getErrorMessage(errorSpecs)}`;
      return <ErrorResponse message={message} dataCy="acfr-items-error" />;
    }

    if (isError) {
      return <ErrorResponse message={getErrorMessage(error)} dataCy="acfr-items-error" />;
    }

    if (isUninitialized) {
      return (
        <div className="text-center text-muted py-5" data-cy="acfr-items-idle">
          Choose a date range and run the report to see the rows behind it.
        </div>
      );
    }

    return (
      <TableView
        id="acfr-items-table"
        columns={columns}
        data={rows}
        isLoading={isFetching}
        emptyMessage="No ACFR items found for the selected period"
        allowPagination
        paginationType="loadmore"
        // TableView offers Load More while the rows shown are fewer than totalCount. There is no
        // total on a cursor walk, only whether another page exists, so one past the rows loaded
        // says "more" and exactly the rows loaded says "done".
        totalCount={rows.length + (nextCursor ? 1 : 0)}
        onLoadMore={handleLoadMore}
        readonly
        bordered
      />
    );
  };

  return (
    <div className="p-2" data-cy="acfr-items-tab" aria-busy={isFetching}>
      <ContainerBanner
        className="mb-3 border rounded"
        color="light"
        icon={faInfoCircle}
        dataCy="acfr-items-notice-bar"
        messages={[
          'One row per budget entry behind the ACFR report · A service funded from two budgets appears twice, in two categories · Rows with no claim or reconciliation reference are approved supplier invoices that were never claimed',
        ]}
      />

      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap" data-cy="acfr-items-controls">
        <DateInput
          type="date"
          id="acfr-items-date-from"
          name="date_from"
          value={dateFrom}
          placeholder="Date from"
          dataCy="acfr-items"
          onChange={(_id, val) => val && setDateFrom(val)}
        />
        <DateInput
          type="date"
          id="acfr-items-date-to"
          name="date_to"
          value={dateTo}
          placeholder="Date to"
          dataCy="acfr-items"
          onChange={(_id, val) => val && setDateTo(val)}
        />
        <Button
          color="success"
          size="sm"
          onClick={handleRunReport}
          disabled={isFetching || isLoadingSpecs || isErrorSpecs || hasIncompleteSpecs}
          dataCy="acfr-items-run-button"
          aria-label="Run Report"
        >
          {isFetching ? <Spinner loading type="beat" size={8} /> : 'Run Report'}
        </Button>
      </div>

      <Toolbar
        id="acfr-items"
        rightCluster={
          <>
            <Button
              size="sm"
              color="success"
              loading={isGenerating}
              disabled={isGenerating || isLoadingSpecs || isErrorSpecs || hasIncompleteSpecs || hasNoExportColumns}
              title={hasNoExportColumns ? 'Choose at least one column to export' : undefined}
              onClick={onGenerateCsv}
              dataCy="acfr-items-export-button"
            >
              <Icon icon={faFileCsv} className="me-2" />
              Generate CSV
            </Button>
            {isColumnPickerEnabled && (
              <Button
                size="sm"
                color="secondary"
                disabled={isLoadingSpecs || isErrorSpecs || hasIncompleteSpecs}
                onClick={() => setIsColumnPickerOpen(true)}
                dataCy="acfr-items-column-picker-button"
              >
                Columns
              </Button>
            )}
          </>
        }
      />

      {renderContent()}

      {isColumnPickerEnabled && (
        <ColumnPickerModal
          isOpen={isColumnPickerOpen}
          setIsOpen={setIsColumnPickerOpen}
          columns={specsData?.columns ?? []}
          groups={COLUMN_GROUPS}
          visibleColumnIds={visibleColumnIds}
          onApply={setVisibleColumnIds}
          dataCy="acfr-items-column-picker"
        />
      )}
    </div>
  );
};

export default ACFRItemsTab;
